import { useCssVars as _useCssVars, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeStyle as _normalizeStyle, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveDynamicComponent as _resolveDynamicComponent, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "k-game absolute-full-cover ios-inset-padding" }
const _hoisted_2 = { class: "k-game-content" }

import * as GameTypes from 'types/game.types';
import { onMounted, onBeforeUnmount, ref, shallowRef, nextTick } from 'vue';
import { useModule } from 'src/modules/modules';
import { deviceEvents, hapticsImpactHeavy } from 'util/device-util';
import PauseMenu from 'scenes/pause-menu.vue';
import GameBackground from 'components/game-background.vue';
import AchievementBanner from 'components/achievement-banner.vue';
import Round from 'src/game/round.vue';
import PickColor from 'src/game/matches/pick-color.vue';
import OddManOut from 'src/game/matches/odd-man-out.vue';
import ColorPhase from 'src/game/matches/color-phase.vue';
import OrderGradient from 'src/game/matches/order-gradient.vue';
import AlignDisks from 'src/game/matches/align-disks.vue';
import ColorByName from 'src/game/matches/color-by-name.vue';
import GameOver from 'src/game/game-over.vue';
import MatchSummary from 'src/game/match-summary.vue';
import PatternMemory from 'src/game/matches/pattern-memory.vue';
import MixColors from 'src/game/matches/mix-colors.vue';
import _ColorTests_ from 'src/game/matches/_color-tests_.vue';
import { clamp } from 'lodash';
import chroma from 'chroma-js';

type ArrayElement<ArrayType extends readonly unknown[]> = 
  ArrayType extends readonly (infer ElementType)[] ? ElementType : never;

/**
 * Keeps track of current game component
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'game',
  setup(__props) {

_useCssVars(_ctx => ({
  "10205c9a": (_unref(theme).values.fontColor)
}))

const { global, theme, game, audio } = useModule();

//---------------------------------------------------------------------------------
//---------------------------------------------------------------------------------
//---------------------------------------------------------------------------------
const devGame : any | null = null;
//game.clock.setDisabled(true);
game.matchState.difficulty = 0;
//---------------------------------------------------------------------------------
//---------------------------------------------------------------------------------
//---------------------------------------------------------------------------------


const activeComponent = shallowRef<any>(devGame ?? Round);
let gameComponents = [PickColor, ColorPhase, OrderGradient, OddManOut, ColorByName, AlignDisks, PatternMemory];
    //gameComponents = [ColorPhase];
    //gameComponents = [OrderGradient];
const gamesComponentsPlayed = shallowRef<any[]>([]);
const lastGameCompPlayed = shallowRef<any>();
const compKey = ref(Math.round(Math.random() * 1000));
const gameOverScreenShown = ref(false);

const achievementBanner = ref<InstanceType<typeof AchievementBanner>>();
onMounted(() => {
    if (achievementBanner.value){
        game.hydrateBannerRef(achievementBanner.value)
    }
});

deviceEvents.on('backButton', game.togglePaused);
onBeforeUnmount(() => {
    deviceEvents.removeListener('backButton', game.togglePaused);    
})


//---------------------------------------------------------------------------------

const enforceDifficulty = () => {
    /**
     * If the `devGame` value is set, increment difficult every match. Should never occur in production.
     */
    if (devGame && game.matchState.round !== 1){
        game.matchState.difficulty = <0 | 1 | 2 | 3 | 4 | 5> clamp(game.matchState.difficulty + 1, 0, 6);
        console.log("Difficulty incremented to", game.matchState.difficulty);
        return;
    }

    switch (true){
        case game.matchState.round <= 7: game.matchState.difficulty = 0; return;
        case game.matchState.round > 7 && game.matchState.round <= 20: game.matchState.difficulty = 1; return;
        case game.matchState.round > 20 && game.matchState.round <= 30: game.matchState.difficulty = 2; return;
        case game.matchState.round > 30 && game.matchState.round <= 40: game.matchState.difficulty = 3; return;
        case game.matchState.round > 40 && game.matchState.round <= 50: game.matchState.difficulty = 4; return;
        case game.matchState.round > 50: game.matchState.difficulty = 5; return;
    }
}

const getNextGame = () : ArrayElement<typeof gameComponents> => {
    enforceDifficulty();

    if (devGame) return devGame; //Dev override for working on a specific game
    if (gamesComponentsPlayed.value.length === gameComponents.length){
        gamesComponentsPlayed.value = [];
    }

    /**
     * In the unlikely scenario that all the colors for a given difficulty level are in use,
     * mark the named-based games as played so that they won't come up anymore. This would probably
     * only potentially occur if the player has reached the highest difficulty and gone for a LOT of matches.
     */
    if (game.areAllNamedColorsUsed() && !gamesComponentsPlayed.value.includes(ColorByName)){
        console.log("Note: Named colors are all in use. Skipping games based on named colors.");
        !gamesComponentsPlayed.value.push(ColorByName);
    }

    /**
     * Get a game that hasn't been played yet in this cycle and is not the same as the last game played
     * (which is only possible at the beginning of a new cycle).
     */
    let comp = Phaser.Utils.Array.GetRandom(gameComponents);
    if (gamesComponentsPlayed.value.includes(comp) || comp === lastGameCompPlayed.value){
        return getNextGame();
    }

    if (!devGame) lastGameCompPlayed.value = comp;
    return comp;
}


const DefaultMatchData : GameTypes.MatchCompleteData = {
    skipped: false,
    background: chroma(theme.theme.gameBg),
    baseScore: 25,
    scoreMultipliers: []
}

const postMatch = async (matchData: GameTypes.MatchCompleteData = DefaultMatchData) => {
    audio.play('FX_success');
    game.clock.pause();
    game.scoreRound(matchData.baseScore ?? 25, matchData.scoreMultipliers ?? []);
    game.setBackgroundColor(matchData.background ?? DefaultMatchData.background!);
    await mountedGameComp.value?.leave?.();
}

const mountedGameComp = ref();
const handleNext = async (matchData?: GameTypes.MatchCompleteData) => {
    if (activeComponent.value === MatchSummary){
        global.startScene('InterstitialScene');
        return;
    }

    /**
     * Go to next stage display
     */
    if (activeComponent.value !== Round){
        await postMatch(matchData);
        game.clock.stop(); //Completely reset clock between rounds so that the user won't see a paused clock on the start of the next round
        gamesComponentsPlayed.value.push(activeComponent.value)
        game.matchState.round += 1;
        await nextTick();
        activeComponent.value = Round;
        return;
    }   
    /**
     * Get a random game component and rotate our key in case it is the currently rendered component already.
     */
    game.resetBackground();
    compKey.value = Math.round(Math.random() * 10000);
    activeComponent.value = <any> getNextGame();
}

const handleFail = (timedOut: boolean) => {
    audio.play('FX_failure');
    hapticsImpactHeavy();
    game.clock.pause();
    game.end(timedOut);
}

const handleFailScreenComplete = async () => {
    await mountedGameComp.value?.leave?.();
    if (game.matchState.score === 0){
        game.backToMenu();
        return;
    }

    activeComponent.value = MatchSummary;
}

game.clock.on('end', () => handleFail(true));

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(GameBackground, {
      style: _normalizeStyle({ background: _unref(theme).gameBg })
    }, null, 8, ["style"]),
    (_unref(game).matchState.gameOver && !gameOverScreenShown.value)
      ? (_openBlock(), _createBlock(GameOver, {
          key: 0,
          onNext: handleFailScreenComplete,
          onLeave: _cache[0] || (_cache[0] = () => gameOverScreenShown.value = true)
        }))
      : _createCommentVNode("", true),
    (_unref(game).paused)
      ? (_openBlock(), _createBlock(PauseMenu, {
          key: 1,
          onUnpause: _unref(game).togglePaused,
          onMenu: _unref(game).backToMenu,
          onEnd: _unref(game).backToMenu,
          onRestart: _unref(game).restartGame
        }, null, 8, ["onUnpause", "onMenu", "onEnd", "onRestart"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_2, [
      (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(activeComponent)), {
        ref_key: "mountedGameComp",
        ref: mountedGameComp,
        context: "rank",
        key: compKey.value,
        difficulty: _unref(game).matchState.difficulty,
        onNext: handleNext,
        onFail: handleFail
      }, null, 40, ["difficulty"]))
    ])
  ]))
}
}

})